"use client";

import React, { useEffect, useRef, ReactNode } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

interface SlidingPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
  slideFrom?: "bottom" | "top" | "left" | "right";
  maxWidth?: string;
  maxHeight?: string;
}

export default function SlidingPanel({
  isOpen,
  onClose,
  title,
  children,
  className = "",
  slideFrom = "bottom",
  maxWidth = "100%",
  maxHeight = "80vh",
}: SlidingPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when panel is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === backdropRef.current) {
      onClose();
    }
  };

  // Handle swipe to close (for mobile)
  useEffect(() => {
    if (!isOpen || slideFrom !== "bottom") return;

    let startY = 0;
    let currentY = 0;
    let isDragging = false;

    const handleTouchStart = (event: TouchEvent) => {
      startY = event.touches[0].clientY;
      isDragging = true;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isDragging) return;

      currentY = event.touches[0].clientY;
      const deltaY = currentY - startY;

      // Only allow downward swipe
      if (deltaY > 0 && panelRef.current) {
        panelRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    };

    const handleTouchEnd = () => {
      if (!isDragging || !panelRef.current) return;

      const deltaY = currentY - startY;
      const threshold = 100; // Minimum distance to close

      if (deltaY > threshold) {
        onClose();
      } else {
        // Snap back to position
        panelRef.current.style.transform = "translateY(0)";
      }

      isDragging = false;
    };

    const panel = panelRef.current;
    if (panel) {
      panel.addEventListener("touchstart", handleTouchStart, { passive: true });
      panel.addEventListener("touchmove", handleTouchMove, { passive: true });
      panel.addEventListener("touchend", handleTouchEnd, { passive: true });
    }

    return () => {
      if (panel) {
        panel.removeEventListener("touchstart", handleTouchStart);
        panel.removeEventListener("touchmove", handleTouchMove);
        panel.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, [isOpen, onClose, slideFrom]);

  // Get slide animation classes
  const getSlideClasses = () => {
    const baseClasses =
      "fixed z-50 bg-background border shadow-lg transition-transform duration-300 ease-out";

    switch (slideFrom) {
      case "bottom":
        return `${baseClasses} bottom-0 left-0 right-0 rounded-t-lg ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`;
      case "top":
        return `${baseClasses} top-0 left-0 right-0 rounded-b-lg ${
          isOpen ? "translate-y-0" : "-translate-y-full"
        }`;
      case "left":
        return `${baseClasses} left-0 top-0 bottom-0 rounded-r-lg ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`;
      case "right":
        return `${baseClasses} right-0 top-0 bottom-0 rounded-l-lg ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`;
      default:
        return baseClasses;
    }
  };

  // Get panel size styles
  const getPanelStyles = (): React.CSSProperties => {
    const styles: React.CSSProperties = {};

    if (slideFrom === "left" || slideFrom === "right") {
      styles.width = maxWidth;
      styles.maxHeight = "100vh";
    } else {
      styles.maxWidth = maxWidth;
      styles.maxHeight = maxHeight;
    }

    return styles;
  };

  if (!isOpen) return null;

  const panelContent = (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        className={`${getSlideClasses()} ${className}`}
        style={getPanelStyles()}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {/* Drag indicator for bottom slide */}
          {slideFrom === "bottom" && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-muted-foreground/30 rounded-full" />
          )}

          <h2 className="text-lg font-semibold text-foreground">
            {title || "Panel"}
          </h2>

          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto p-4"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Render in portal to avoid z-index issues
  return typeof document !== "undefined"
    ? createPortal(panelContent, document.body)
    : null;
}
